import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "../../../context/TranslationContext";
import VideoQuestion from "../VideoQuestion";
import { Cards } from "../../../Cards";
import NewVideoQuestionButton from "../NewVideoQuestionButton";
import fetchCustom from "../../../../wp_helpers/fetchCustom";
import getResponseJson from "../../../../wp_helpers/getResponseJson";
import { CREATOR_ANYONE, CREATOR_ME, filterVideoQuestions, shouldShowFilterBar } from "./filterVideoQuestions";

const SEARCH_DEBOUNCE_MS = 300;
// Mirrors the accessor's VIDEO_LIBRARY_LISTING_ORDER_NEWEST/OLDEST - sent as
// the `order` query param so sorting applies across pages, not just the one
// already fetched.
const SORT_NEWEST_FIRST = 'newest';
const SORT_OLDEST_FIRST = 'oldest';

/**
 * The `video_library_questions_data` accessor always wraps its results as
 * `{results, paginate}` - except when the caller lacks permission, where the
 * framework swaps in `UnconfiguredDataSourceAccessor`, whose EDIT_MODE
 * fallback is a bare list (see `video_library_data_source_actions.py`'s
 * module docstring). Normalizing both here keeps that framework detail out
 * of the component.
 */
const normalizeVideoLibraryResponse = (json) => (
    Array.isArray(json) ? { results: json, more: false } : { results: json.results || [], more: !!json.paginate?.more }
);

const withQueryParam = (url, name, value) => {
    if (value === undefined || value === null || value === '') return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${name}=${encodeURIComponent(value)}`;
};

/**
 * Renders one scope tab's worth of video questions (My Videos, or Shared Library).
 * `listingUrl` already carries the scope/view_mode the data source needs; this adds
 * `term` (debounced search) and `page` (Load more) and lets the server do search/
 * paging/sort instead of fetching everything and filtering client-side.
 */
const VideoQuestionsList = ({
    listingUrl,
    createVideoQuestionFormApi,
    showScopeChoiceOnCreate,
    showCreatorFilter,
    myVideosApiForCreatorFilter,
}) => {
    const { t } = useTranslations();
    const [videoQuestions, setVideoQuestions] = useState([]);
    const [videoQuestionsFetched, setVideoQuestionsFetched] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(1);
    // ids the viewer owns - only meaningful (and only fetched) on a tab that shows the
    // creator filter, i.e. Shared Library. "My Videos" is already all-mine.
    const [myVideoIds, setMyVideoIds] = useState(null);
    // One object keyed by filter id, so a later swap to the design-system `Filters`
    // component is additive rather than a rewrite of the state model.
    const [filters, setFilters] = useState({ term: '', creator: CREATOR_ANYONE, sort: SORT_NEWEST_FIRST });
    // The term the shown results were fetched with -- `term` is applied server-side, so it
    // lags the typed one by a debounce plus a request.
    const [appliedTerm, setAppliedTerm] = useState('');
    const debounceRef = useRef(null);
    const skipNextTermEffect = useRef(true);
    const skipNextSortEffect = useRef(true);

    const fetchPage = async (pageToFetch, { append } = {}) => {
        if (!listingUrl) return;
        const term = filters.term.trim();
        const url = withQueryParam(
            withQueryParam(withQueryParam(listingUrl, 'term', term), 'order', filters.sort),
            'page', pageToFetch
        );
        const response = await fetchCustom(url);
        const responseJson = await getResponseJson(response);
        if (!response.ok) return;
        setAppliedTerm(term);
        const { results, more } = normalizeVideoLibraryResponse(responseJson);
        setVideoQuestions((previous) => (append ? [...previous, ...results] : results));
        setHasMore(more);
        setVideoQuestionsFetched(true);
    };

    const getVideoQuestions = async () => {
        setPage(1);
        await fetchPage(1);
    };

    const loadMore = async () => {
        const nextPage = page + 1;
        setPage(nextPage);
        await fetchPage(nextPage, { append: true });
    };

    const getMyVideoIds = async () => {
        if (!myVideosApiForCreatorFilter) return;
        const response = await fetchCustom(myVideosApiForCreatorFilter);
        const responseJson = await getResponseJson(response);
        if (response.ok) {
            const { results } = normalizeVideoLibraryResponse(responseJson);
            setMyVideoIds(new Set(results.map((item) => item.id)));
        }
    };

    useEffect(() => {
        // Tabs reuses this same component for both scopes (see VideoQuestionsLibrary),
        // so switching tabs re-renders rather than remounts - reset before refetching so
        // the previous tab's cards don't flash while the new tab's request is in flight.
        setVideoQuestions([]);
        setVideoQuestionsFetched(false);
        setHasMore(false);
        setPage(1);
        setMyVideoIds(null);
        skipNextTermEffect.current = true; // the fetch below covers the mount/tab-switch case
        skipNextSortEffect.current = true;
        getVideoQuestions();
        if (showCreatorFilter) getMyVideoIds();
        // Deliberately re-runs only when the listing URL identity changes (i.e. the active
        // scope tab changed) - getVideoQuestions/getMyVideoIds are re-created each render
        // but aren't meant to re-trigger the fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingUrl]);

    useEffect(() => {
        // Debounced search-as-you-type: the term lives server-side now (the
        // accessor's `term` param), not as a client-side substring filter.
        // Skipped right after a tab switch/mount, which already fetched above.
        if (skipNextTermEffect.current) {
            skipNextTermEffect.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            fetchPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.term]);

    useEffect(() => {
        // Sort is a discrete choice, not typing - refetch immediately rather
        // than debouncing. Skipped right after a tab switch/mount.
        if (skipNextSortEffect.current) {
            skipNextSortEffect.current = false;
            return;
        }
        setPage(1);
        fetchPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.sort]);

    const visibleVideoQuestions = useMemo(
        () => filterVideoQuestions(videoQuestions, { creatorFilter: filters.creator, myVideoIds }),
        [videoQuestions, filters.creator, myVideoIds]
    );

    // CREATOR_ANYONE where the control is not rendered: filters survive a tab switch, so a
    // creator picked on Shared Library must not count as narrowing My Videos.
    const creatorFilter = showCreatorFilter ? filters.creator : CREATOR_ANYONE;
    const showFilterBar = shouldShowFilterBar({
        fetched: videoQuestionsFetched,
        resultCount: videoQuestions.length,
        typedTerm: filters.term,
        appliedTerm,
        creatorFilter,
    });

    const NewVideoQuestionButtonComponent = () => (
        createVideoQuestionFormApi ? (
            <div className="btn-container">
                <NewVideoQuestionButton
                    createVideoQuestionFormApi={createVideoQuestionFormApi}
                    getVideoQuestions={getVideoQuestions}
                    showScopeChoice={showScopeChoiceOnCreate}
                />
            </div>
        ) : null
    );

    return (
        <>
            {showFilterBar ? (
                <>
                    <div className="margin-vertical-xl flex flex-items-center flex-content-space-between gap-1x flex-wrap">
                        <div className="flex flex-items-center gap-1x flex-wrap">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('Search by name')}
                                value={filters.term}
                                onChange={(e) => setFilters((prev) => ({ ...prev, term: e.target.value }))}
                            />
                            {showCreatorFilter && (
                                <select
                                    className="form-control"
                                    value={filters.creator}
                                    onChange={(e) => setFilters((prev) => ({ ...prev, creator: e.target.value }))}
                                    aria-label={t('Creator')}
                                >
                                    <option value={CREATOR_ANYONE}>{t('Anyone')}</option>
                                    <option value={CREATOR_ME}>{t('Me')}</option>
                                </select>
                            )}
                            <select
                                className="form-control"
                                value={filters.sort}
                                onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}
                                aria-label={t('Sort by date')}
                            >
                                <option value={SORT_NEWEST_FIRST}>{t('Newest first')}</option>
                                <option value={SORT_OLDEST_FIRST}>{t('Oldest first')}</option>
                            </select>
                        </div>
                        <NewVideoQuestionButtonComponent />
                    </div>
                    {visibleVideoQuestions.length ? (
                        <>
                            <Cards>
                                {visibleVideoQuestions.map((videoQuestion) => (
                                    <VideoQuestion
                                        key={videoQuestion.id}
                                        getVideoQuestions={getVideoQuestions}
                                        createVideoQuestionFormApi={createVideoQuestionFormApi}
                                        {...videoQuestion}
                                    />
                                ))}
                            </Cards>
                            {hasMore && (
                                <div className="margin-vertical-xl flex flex-content-center">
                                    <button type="button" className="btn btn-default" onClick={loadMore}>
                                        {t('Load more')}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="margin-vertical-xl">{t('No video questions match the current filters.')}</div>
                    )}
                </>
            ) : videoQuestionsFetched ? (
                <>
                    <div className="margin-vertical-xl">{t('There are no video questions yet.')}</div>
                    <NewVideoQuestionButtonComponent />
                </>
            ) : null}
        </>
    );
};

export default VideoQuestionsList;
